/**
 * Shop Board: Unassigned + one column per mechanic. Drag a card to a mechanic to assign
 * (reassign asks for a reason); drag within a column to set the queue order.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { closestCorners, DndContext, PointerSensor, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Board, Me, WorkOrderCard } from '@wo/shared';
import { api, errorText } from '../lib/api';
import { ago } from '../lib/format';
import { Button, ErrorText, Field, Loading, Modal, PageHeader, StatusBadge, Textarea } from '../components/ui';
import { can } from '../lib/perm';

const UNASSIGNED = 'col:unassigned';
const colId = (mechanicId: number) => `col:${mechanicId}`;

function Card({ card, draggable, onResume }: { card: WorkOrderCard; draggable: boolean; onResume?: (c: WorkOrderCard) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: card.id, disabled: !draggable });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      {...attributes}
      {...listeners}
      className={`rounded-md border border-slate-200 bg-white p-3 shadow-sm ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      <div className="flex items-center justify-between gap-2">
        <Link to={`/work-orders/${card.id}`} className="font-bold text-blue-800 hover:underline" onPointerDown={(e) => e.stopPropagation()}>
          #{card.number}
        </Link>
        <span className="flex items-center gap-1">
          {card.clockedIn && <span className="h-2.5 w-2.5 rounded-full bg-green-600" title="Clocked in now" />}
          {(card.openIssues ?? 0) > 0 && <span className="rounded bg-amber-500 px-1.5 text-xs font-bold text-white" title="Additional issue waiting">!{card.openIssues}</span>}
          <StatusBadge status={card.status} />
        </span>
      </div>
      <div className="mt-1 text-sm font-medium text-slate-800">{card.customerName}</div>
      <div className="text-xs text-slate-600">{card.machine}</div>
      <div className="mt-1 flex justify-between text-xs text-slate-500">
        <span>{card.jobType}</span>
        <span>{ago(card.openedAt)}</span>
      </div>
      {card.holdReason && <div className="mt-1 text-xs font-semibold text-orange-800">On hold: {card.holdReason}</div>}
      {/* Change request 2026-11: the Manager takes a job off hold right from the board. */}
      {onResume && card.status === 'ON_HOLD' && (
        <button
          type="button"
          className="mt-1 rounded border border-orange-300 bg-orange-50 px-2 py-0.5 text-xs font-semibold text-orange-900 hover:bg-orange-100"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onResume(card)}
        >
          Resume
        </button>
      )}
      {card.hoursToday > 0 && <div className="text-xs text-slate-600">{card.hoursToday.toFixed(1)} h today</div>}
      {(card.helpers?.length ?? 0) > 0 && <div className="mt-1 text-xs text-slate-700">+ {card.helpers!.map((h) => h.name).join(', ')}</div>}
    </div>
  );
}

function Column({ id, title, cards, draggable, sub, helping = [], onResume }: { id: string; title: string; cards: WorkOrderCard[]; draggable: boolean; sub?: ReactNode; helping?: WorkOrderCard[]; onResume?: (c: WorkOrderCard) => void }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div className="flex w-72 shrink-0 flex-col rounded-lg bg-slate-200/70">
      <h2 className="flex justify-between px-3 py-2 text-sm font-bold text-slate-700">
        <span>{title}{sub}</span>
        <span className="text-slate-500">{cards.length}</span>
      </h2>
      <SortableContext id={id} items={cards.map((c) => c.id)} strategy={verticalListSortingStrategy}>
        <div ref={setNodeRef} className={`flex min-h-24 flex-1 flex-col gap-2 p-2 ${isOver ? 'bg-blue-100' : ''}`}>
          {cards.map((c) => <Card key={c.id} card={c} draggable={draggable} onResume={onResume} />)}
        </div>
      </SortableContext>
      {helping.length > 0 && (
        <div className="border-t border-slate-300 px-3 py-2 text-xs text-slate-600">
          <span className="font-semibold">Also helping on:</span>{' '}
          {helping.map((c, i) => (
            <span key={c.id}>
              {i > 0 && ', '}
              <Link to={`/work-orders/${c.id}`} className="text-blue-800 hover:underline">#{c.number}</Link>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function BoardPage({ me }: { me: Me }) {
  const qc = useQueryClient();
  const board = useQuery({ queryKey: ['board'], queryFn: () => api.get<Board>('/api/board'), refetchInterval: 30_000 });
  const canAssign = can(me, 'jobs.assign');
  const canResume = can(me, 'jobs.hold');
  const [error, setError] = useState<string | null>(null);
  const [pendingReassign, setPendingReassign] = useState<{ card: WorkOrderCard; to: number; toName: string } | null>(null);
  const [reason, setReason] = useState('');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const columnOf = useMemo(() => {
    const m = new Map<number, string>();
    board.data?.unassigned.forEach((c) => m.set(c.id, UNASSIGNED));
    board.data?.mechanics.forEach((col) => col.jobs.forEach((c) => m.set(c.id, colId(col.user.id))));
    return m;
  }, [board.data]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['board'] });
  const run = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onError: (e) => setError(errorText(e)),
    onSettled: refresh,
  });

  if (board.isLoading || !board.data) return <Loading />;
  const data = board.data;

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || !canAssign) return;
    setError(null);
    const cardId = Number(active.id);
    const from = columnOf.get(cardId)!;
    const to = typeof over.id === 'string' ? over.id : columnOf.get(Number(over.id))!;
    const card = [...data.unassigned, ...data.mechanics.flatMap((m) => m.jobs)].find((c) => c.id === cardId)!;

    if (card.status === 'ON_HOLD' && from !== to) {
      setError(`#${card.number} is on hold — press Resume on the card first, then move it.`);
      return;
    }
    if (to === UNASSIGNED) {
      if (from !== UNASSIGNED) setError('A job can be moved to another mechanic, but not back to Unassigned.');
      return;
    }
    const mechanicId = Number(to.slice(4));
    const mech = data.mechanics.find((m) => m.user.id === mechanicId)!;

    if (from === to) {
      const ids = mech.jobs.map((j) => j.id);
      const overIndex = typeof over.id === 'number' ? ids.indexOf(Number(over.id)) : ids.length - 1;
      const next = arrayMove(ids, ids.indexOf(cardId), overIndex);
      if (next.join() !== ids.join()) run.mutate(() => api.post('/api/board/reorder', { mechanicId, workOrderIds: next }));
    } else if (from === UNASSIGNED) {
      run.mutate(() => api.post(`/api/work-orders/${cardId}/transition`, { action: 'assign', mechanicId }));
    } else {
      setReason('');
      setPendingReassign({ card, to: mechanicId, toName: mech.user.name });
    }
  };

  return (
    <>
      <PageHeader title="Shop Board">
        <Link to="/work-orders/new"><Button>New Work Order</Button></Link>
      </PageHeader>
      <ErrorText error={error} />
      {!canAssign && <p className="mb-2 text-sm text-slate-500">Only a Manager can assign or reorder jobs.</p>}
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4">
          <Column id={UNASSIGNED} title="Unassigned" cards={data.unassigned} draggable={canAssign} />
          {data.mechanics.map((m) => (
            <Column
              key={m.user.id}
              id={colId(m.user.id)}
              title={m.user.name}
              cards={m.jobs}
              helping={m.helping}
              draggable={canAssign}
              onResume={canResume ? (c) => run.mutate(() => api.post(`/api/work-orders/${c.id}/transition`, { action: 'resume' })) : undefined}
              sub={
                <span className="ml-2 text-xs font-normal text-slate-500">
                  {m.hoursToday.toFixed(1)} h today
                  {m.lockedUntil && <span className="ml-1 rounded bg-amber-200 px-1 font-semibold text-amber-900">PIN locked</span>}
                  {m.onTime && <span className="ml-1 rounded bg-slate-300 px-1 font-semibold text-slate-800">On {m.onTime}</span>}
                </span>
              }
            />
          ))}
          {data.mechanics.length === 0 && (
            <p className="self-center p-6 text-sm text-slate-500">No mechanics yet. An Admin adds them under Users.</p>
          )}
        </div>
      </DndContext>

      {pendingReassign && (
        <Modal
          title={`Reassign #${pendingReassign.card.number} to ${pendingReassign.toName}`}
          onClose={() => setPendingReassign(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setPendingReassign(null)}>Cancel</Button>
              <Button
                disabled={reason.trim().length < 3}
                onClick={() => {
                  const { card, to } = pendingReassign;
                  setPendingReassign(null);
                  run.mutate(() => api.post(`/api/work-orders/${card.id}/transition`, { action: 'reassign', mechanicId: to, reason }));
                }}
              >
                Reassign
              </Button>
            </>
          }
        >
          <Field label="Reason" required>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus rows={3} />
          </Field>
        </Modal>
      )}
    </>
  );
}
